import { useEffect, useState } from 'react'
import { X, Loader2, Check, AlertCircle, RefreshCw } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { API_CONFIG } from '../lib/config'

interface SyncStep {
    step: number
    name: string
    status: 'pending' | 'in_progress' | 'completed' | 'error'
    message?: string
    startTime?: Date
    endTime?: Date
}

interface SyncModalProps {
    isOpen: boolean
    onClose: () => void
    accessToken: string
}

export function SyncModal({ isOpen, onClose, accessToken }: SyncModalProps) {
    const queryClient = useQueryClient()
    const [steps, setSteps] = useState<SyncStep[]>([
        { step: 1, name: 'XML Ürün Senkronizasyonu', status: 'pending' },
        { step: 2, name: 'Stok Snapshot ve Farklar', status: 'pending' },
        { step: 3, name: 'İstatistikler Güncelleme', status: 'pending' },
        { step: 4, name: 'Sipariş Senkronizasyonu', status: 'pending' },
        { step: 5, name: 'Ürünler Yükleniyor', status: 'pending' }
    ])
    const [isCompleted, setIsCompleted] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isSyncing, setIsSyncing] = useState(false)

    useEffect(() => {
        if (isOpen && !isSyncing) {
            startSync()
        }
    }, [isOpen])

    const loadAllProducts = async () => {
        try {
            setSteps(prev => prev.map(s =>
                s.step === 5 ? { ...s, status: 'in_progress' as const } : s
            ))

            const allProducts: any[] = []
            let page = 1
            const pageSize = 100
            let hasMore = true

            // Tüm ürünleri sayfalama ile yükle
            while (hasMore && page <= 350) {
                const response = await fetch(
                    `${API_CONFIG.BASE_URL}/api/products?page=${page}&size=${pageSize}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                        },
                    }
                )

                if (!response.ok) {
                    throw new Error('Ürünler yüklenemedi')
                }

                const data = await response.json()

                if (data.products && data.products.length > 0) {
                    allProducts.push(...data.products)
                    hasMore = data.products.length === pageSize
                    page++
                } else {
                    hasMore = false
                }
            }

            // Query cache'i güncelle
            queryClient.setQueryData(['allProducts'], allProducts)

            // Velocity ve diğer cache'leri de invalidate et
            queryClient.invalidateQueries({ queryKey: ['velocityData'] })

            setSteps(prev => prev.map(s =>
                s.step === 5 ? {
                    ...s,
                    status: 'completed' as const,
                    message: `${allProducts.length} ürün yüklendi ve cache'lendi`
                } : s
            ))

        } catch (error) {
            console.error('Ürün yükleme hatası:', error)
            setSteps(prev => prev.map(s =>
                s.step === 5 ? {
                    ...s,
                    status: 'error' as const,
                    message: 'Ürünler yüklenemedi'
                } : s
            ))
            throw error
        }
    }

    const startSync = async () => {
        setIsSyncing(true)
        setIsCompleted(false)
        setError(null)

        // Reset steps
        setSteps([
            { step: 1, name: 'XML Ürün Senkronizasyonu', status: 'pending' },
            { step: 2, name: 'Stok Snapshot ve Farklar', status: 'pending' },
            { step: 3, name: 'İstatistikler Güncelleme', status: 'pending' },
            { step: 4, name: 'Sipariş Senkronizasyonu', status: 'pending' },
            { step: 5, name: 'Ürünler Yükleniyor', status: 'pending' }
        ])

        let backendCompleted = false
        const timeout = 5 * 60 * 1000 // 5 dakika timeout
        const controller = new AbortController()
        const timeoutId = setTimeout(() => {
            controller.abort()
        }, timeout)

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/sync/full-sync`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                signal: controller.signal
            })

            clearTimeout(timeoutId)

            if (!response.ok) {
                throw new Error('Senkronizasyon başlatılamadı')
            }

            const reader = response.body?.getReader()
            const decoder = new TextDecoder()

            if (!reader) {
                throw new Error('Stream reader oluşturulamadı')
            }

            // Stream okuma için ayrı timeout
            const streamTimeout = setTimeout(() => {
                reader.cancel()
                throw new Error('Senkronizasyon zaman aşımına uğradı. İşlem arka planda devam ediyor olabilir.')
            }, timeout)

            try {
                while (true) {
                    const { done, value } = await reader.read()

                    if (done) {
                        clearTimeout(streamTimeout)
                        break
                    }

                    const chunk = decoder.decode(value)
                    const lines = chunk.split('\n')

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(line.slice(6))

                                if (data.steps) {
                                    setSteps(data.steps)
                                }

                                if (data.completed) {
                                    backendCompleted = true
                                    clearTimeout(streamTimeout)
                                }

                                if (data.error) {
                                    clearTimeout(streamTimeout)
                                    setError(data.error)
                                    setIsSyncing(false)
                                    return
                                }
                            } catch (parseError) {
                                // JSON parse hatası, devam et
                                console.warn('Stream parse hatası:', parseError)
                            }
                        }
                    }
                }
            } catch (streamError) {
                clearTimeout(streamTimeout)
                if (streamError instanceof Error && streamError.name === 'AbortError') {
                    throw new Error('Senkronizasyon zaman aşımına uğradı. İşlem arka planda devam ediyor olabilir.')
                }
                throw streamError
            }

            // Stream tamamlandı, şimdi ürünleri yükle
            if (backendCompleted) {
                await loadAllProducts()
                setIsCompleted(true)
            }

            setIsSyncing(false)
        } catch (err) {
            clearTimeout(timeoutId)
            console.error('Senkronizasyon hatası:', err)
            
            let errorMessage = 'Bilinmeyen hata'
            if (err instanceof Error) {
                if (err.name === 'AbortError' || err.message.includes('zaman aşımı')) {
                    errorMessage = 'Senkronizasyon zaman aşımına uğradı. İşlem arka planda devam ediyor olabilir. Lütfen birkaç dakika sonra sayfayı yenileyin.'
                } else if (err.message.includes('NetworkError') || err.message.includes('Failed to fetch')) {
                    errorMessage = 'Ağ hatası oluştu. İşlem arka planda devam ediyor olabilir. Lütfen birkaç dakika sonra sayfayı yenileyin.'
                } else {
                    errorMessage = err.message
                }
            }
            
            setError(errorMessage)
            setIsSyncing(false)
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <Check className="w-6 h-6 text-green-500" />
            case 'in_progress':
                return <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            case 'error':
                return <AlertCircle className="w-6 h-6 text-red-500" />
            default:
                return <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
        }
    }

    const canClose = !isSyncing || isCompleted || error !== null

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center gap-3">
                        <RefreshCw className={`w-6 h-6 ${isSyncing ? 'animate-spin text-blue-500' : 'text-gray-600'}`} />
                        <h2 className="text-xl font-bold text-gray-800">
                            Tam Senkronizasyon
                        </h2>
                    </div>
                    {canClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    )}
                </div>

                <div className="p-6">
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                            <div className="text-red-700 text-sm">{error}</div>
                        </div>
                    )}

                    {isCompleted && !error && (
                        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <div className="text-green-700 text-sm font-medium">
                                Tüm veriler başarıyla senkronize edildi!
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        {steps.map((step, index) => (
                            <div
                                key={step.step}
                                className={`p-4 rounded-lg border-2 transition-all ${step.status === 'completed'
                                    ? 'bg-green-50 border-green-200'
                                    : step.status === 'in_progress'
                                        ? 'bg-blue-50 border-blue-300 shadow-md'
                                        : step.status === 'error'
                                            ? 'bg-red-50 border-red-200'
                                            : 'bg-gray-50 border-gray-200'
                                    }`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className="flex-shrink-0">
                                        {getStatusIcon(step.status)}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-700">
                                                {step.step}.
                                            </span>
                                            <span className="font-medium text-gray-800">
                                                {step.name}
                                            </span>
                                        </div>
                                        {step.message && (
                                            <p className="text-sm text-gray-600 mt-1">
                                                {step.message}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {!isSyncing && !isCompleted && !error && (
                        <div className="mt-6 text-center text-gray-500 text-sm">
                            Senkronizasyon başlatılıyor...
                        </div>
                    )}
                </div>

                <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                    {canClose && (
                        <button
                            onClick={onClose}
                            className={`px-6 py-2 rounded-lg font-medium transition-colors ${isCompleted
                                ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                }`}
                        >
                            {isCompleted ? 'Tamamlandı' : 'Kapat'}
                        </button>
                    )}
                    {!canClose && (
                        <div className="text-sm text-gray-500">
                            Senkronizasyon devam ediyor, lütfen bekleyin...
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

